'use es6';

import { useState, useEffect } from 'react';
import { fromJS } from 'immutable';
import compose from 'transmute/compose';
import styled from 'styled-components';
import MapProps from 'reporting-ui-components/components/shared/MapProps';
import ResolveReferences from 'reference-resolvers/ResolveReferences';
import { isLoading, isError } from 'reference-resolvers/utils';
import { PERSONA } from 'reference-resolvers/constants/ReferenceObjectTypes';
import * as ReferenceTypes from 'reference-resolvers/schema/ReferenceTypes';
import { CONTACTS } from 'reporting-data/constants/dataTypes';
import PersonaLabel from './PersonaLabel';
import PersonaCountLabel from './PersonaCountLabel';
import { Table } from 'foundations-components/transitional/components/Table';
import NoData from 'reporting-ui-components/components/shared/NoData';
import { getTableRows, getPersonaAggregation } from './PersonaUtils';
import { reportsEqual } from 'reporting-ui-components/lib/shallowCompareReports';
import countProperty from 'reporting-data/properties/partial/count-property';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { resolve } from 'reporting-data/main';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import { getPropertyLabel } from 'reporting-data/v2/dataset/datasetRecords';
import PropTypes from 'prop-types';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTr = styled.tr.withConfig({
  displayName: "Persona__StyledTr",
  componentId: "i1me43-0"
})(["height:50px;"]);
const StyledTd = styled.td.withConfig({
  displayName: "Persona__StyledTd",
  componentId: "i1me43-1"
})(["padding-block:", ";"], ({
  theme
}) => theme.space['400']);
const StyledTdRight = styled(StyledTd).withConfig({
  displayName: "Persona__StyledTdRight",
  componentId: "i1me43-2"
})(["text-align:right;"]);
const StyledTh = styled.th.withConfig({
  displayName: "Persona__StyledTh",
  componentId: "i1me43-3"
})(["text-align:right !important;"]);
const resolveReferences = ResolveReferences(resolvers => {
  return {
    personas: resolvers[PERSONA].all()
  };
});
const mapReport = MapProps(props => {
  return Object.assign({}, props, {
    report: getPersonaAggregation(props.report)
  });
});
const PersonaVizView = ({
  personas,
  report,
  setIsRendered,
  noDataRenderer,
  onReportResolve,
  onError
}) => {
  const [currentReport, setCurrentReport] = useState();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!currentReport || !reportsEqual(report, currentReport)) {
      setCurrentReport(report);
      resolve(fromJS(report), {
        v2Datasets: true
      }).then(response => {
        if (typeof setIsRendered === 'function') {
          setIsRendered();
        }
        setData(response);
        if (onReportResolve) {
          onReportResolve({
            data: response
          });
        }
      }).catch(error => {
        if (onError) {
          onError(error);
        } else {
          throw error;
        }
      });
    }
  }, [currentReport, report, onError, onReportResolve, setIsRendered]);
  if (isLoading(personas) || !data) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true
    });
  } else if (isError(personas)) {
    throw personas.reason;
  }
  const personaLabel = getPropertyLabel(data.getIn(['primary', 'data']), 'hs_persona');
  const countOfContactsLabel = countProperty(CONTACTS).getIn(['count', 'label']);
  const total = data.getIn(['summary', 'data', 'data', 0, 'SUM|count']);
  const tableData = getTableRows(data, personas);
  if (tableData.length === 0) {
    return noDataRenderer ? noDataRenderer() : /*#__PURE__*/_jsx(NoData, {});
  }
  return /*#__PURE__*/_jsx("div", {
    style: {
      overflow: 'auto',
      maxHeight: '100%'
    },
    children: /*#__PURE__*/_jsxs(Table, {
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs(StyledTr, {
          children: [/*#__PURE__*/_jsx("th", {
            children: personaLabel
          }), /*#__PURE__*/_jsx(StyledTh, {
            children: countOfContactsLabel
          })]
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: tableData.map(({
          count,
          personaId,
          personaLabel: label
        }) => {
          return /*#__PURE__*/_jsxs(StyledTr, {
            children: [/*#__PURE__*/_jsx(StyledTd, {
              children: /*#__PURE__*/_jsx(PersonaLabel, {
                personaId: personaId,
                personaLabel: label
              })
            }), /*#__PURE__*/_jsx(StyledTdRight, {
              children: /*#__PURE__*/_jsx(PersonaCountLabel, {
                count: count,
                total: total
              })
            })]
          }, personaId);
        })
      })]
    })
  });
};
PersonaVizView.propTypes = {
  personas: ReferenceTypes.all.isRequired,
  report: ReportType.isRequired,
  onError: PropTypes.func,
  onReportResolve: PropTypes.func,
  noDataRenderer: PropTypes.func,
  setIsRendered: PropTypes.func
};
const enhance = compose(mapReport, resolveReferences);
export default enhance(PersonaVizView);